<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Building2, Check, Globe, MapPin, TrendingUp, Users } from 'lucide-svelte';

  import { fetchIndustryDetails, LEGAL_FORM, type IndustryDetails } from '$lib/api/industry';
  import { logger } from '$lib';
  import ScreenView from '$lib/components/ScreenView.svelte';
  import SizeBands from '$lib/components/Guided/SizeBands.svelte';
  import SwissMap from '$lib/components/Guided/SwissMap.svelte';

  /**
   * What is actually in a category, before the customer pays to find out.
   *
   * # Why a category needed a screen at all
   *
   * The picker gives a name and a number. That is enough to choose between two
   * categories by size and nothing else — not whether we can reach them, not
   * whether they are the twenty-person firms the customer sells to or the
   * one-person GmbHs that share the industry code, not whether they are in the
   * half of the country the customer works in. Those are the questions somebody
   * has before they buy, and until now the only way to answer them was to buy.
   *
   * # It sells nothing and names nobody
   *
   * Every figure here is a count over a population. No company is named, no
   * contact is named, and the executive figure is a count of changes rather
   * than a list of people — so this can be read by anyone, and the endpoint
   * behind it needs no Turnstile token. It is also why the panel is worth
   * showing before the sale rather than after: there is nothing in it to
   * resell.
   *
   * # Reach comes first
   *
   * Not size, and not the map. A category of 17,441 companies where 10,243 have
   * a website and 4,355 a telephone number is a different product from one
   * where those numbers match, and it is the least abstract thing on the screen
   * for somebody deciding whether this is worth money.
   */

  interface Props {
    /** Which category to profile. */
    centroidId: number;

    /** Its name, so the heading is right before the response arrives. */
    name: string;

    /** Whether it is currently in the customer's selection. */
    selected?: boolean;

    /** Take or drop the category from here, or undefined to show no button. */
    onToggle?: () => void;

    onClose: () => void;
  }

  let { centroidId, name, selected = false, onToggle, onClose }: Props = $props();

  let details = $state<IndustryDetails | null>(null);
  let loading = $state(true);
  let failed = $state(false);

  $effect(() => {
    const id = centroidId;
    let current = true;

    loading = true;
    failed = false;

    fetchIndustryDetails(id)
      .then((result) => {
        // A second category can be opened before the first has answered. The
        // late response must not overwrite the one being read.
        if (!current) return;
        details = result;
        loading = false;
      })
      .catch((error) => {
        if (!current) return;
        logger.warn('industry profile: failed', error);
        failed = true;
        loading = false;
      });

    return () => {
      current = false;
    };
  });

  /** Escape closes, as it does on every other dialog here. */
  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') onClose();
  }

  /**
   * Numbers in Swiss notation, at every locale.
   *
   * Not svelte-i18n's `$number`, which formats for the current locale — and the
   * German one is registered as `de`, so it groups with a full stop: CHF 20.000
   * where Switzerland writes 20'000. The panel opens from IndustryTree, whose
   * counts are already `de-CH`, so `$number` also put two separators for the
   * same quantity a few pixels apart.
   *
   * Unconditional rather than switched on the locale, for the same reason the
   * tree is: the figures are Swiss company data and the apostrophe is how they
   * are written here, whichever language the customer is reading.
   */
  const swiss = (value: number | undefined) => (value ?? 0).toLocaleString('de-CH');

  const total = $derived(details?.company_count ?? 0);

  /** A count as a whole percent of the category. */
  const share = (count: number) => (total > 0 ? Math.round((100 * count) / total) : 0);

  /**
   * The bands as shares, which is what the chart draws.
   *
   * The service sends counts. It sends no denominator with them and does not
   * need to: a headcount is derived from the register for every company, so the
   * bands cover the category and `company_count` is what they are out of.
   */
  const bands = $derived(
    (details?.sizes.bands ?? []).map((b) => ({
      band: b.band,
      share_of_list: total > 0 ? b.count / total : 0
    }))
  );

  /** The map wants abbreviations and counts under its own field names. */
  const spread = $derived(
    (details?.cantons ?? []).map((c) => ({ canton: c.name, companies: c.count }))
  );

  /**
   * Three rows and a remainder, in a fixed order.
   *
   * # Why the register's own list is not the list to show
   *
   * The service sends every form it found, largest first, and the register
   * numbers eleven of them. Drawn straight, that is a panel whose order changes
   * between categories and whose tail is codes nobody here has a word for —
   * and a guessed label on a screen somebody is buying from is worse than a
   * number.
   *
   * What a customer is actually asking is which of two or three kinds of
   * company this category is. So: the corporation and the limited-liability
   * company on their own, because between them they are almost the whole of
   * commercial Switzerland and they are what the capital line below is about;
   * sole proprietorships and partnerships together, because the distinction
   * between them does not change who a customer can sell to; and everything
   * else summed rather than enumerated.
   *
   * The order is fixed rather than by size, so the same row means the same
   * thing when the customer opens the next category.
   */
  const forms = $derived.by(() => {
    const found = details?.legal_forms ?? [];
    const countOf = (form: number) => found.find((f) => f.form === form)?.count ?? 0;

    const named = [
      { key: 'corporation', count: countOf(LEGAL_FORM.corporation) },
      { key: 'limited_liability', count: countOf(LEGAL_FORM.limitedLiability) },
      {
        key: 'partnerships',
        count: countOf(LEGAL_FORM.soleProprietorship) + countOf(LEGAL_FORM.partnership)
      }
    ];

    // The remainder is the seven forms with no row of their own plus
    // legal_forms_other, which the service already uses for companies the
    // register left without a form and for cells too small to name.
    const grouped: number[] = Object.values(LEGAL_FORM);
    const rest =
      found.filter((f) => !grouped.includes(f.form)).reduce((sum, f) => sum + f.count, 0) +
      (details?.legal_forms_other ?? 0);

    return {
      rows: named.filter((row) => row.count > 0),
      other: rest
    };
  });

  /**
   * The last twelve completed years, oldest first, drawn as a distribution.
   *
   * # The service sends them newest first
   *
   * `founding` is `ORDER BY year DESC` and twenty years long, so taking the
   * tail gives 2006 to 2017 and drawing it unreversed runs time backwards
   * across the chart. Both were wrong here until this was opened against real
   * data: the panel showed a decade nobody asked about, under an axis whose
   * left-hand label was the later year.
   *
   * `founding_months` is ascending, which is the other reason to state the
   * direction rather than assume the two agree.
   *
   * Completed years only. The running year is deliberately not appended: eight
   * months against twelve reads as a category collapsing rather than as a year
   * that has not finished.
   */
  const years = $derived([...(details?.founding ?? [])].slice(0, 12).reverse());

  /** How many of the running year we have so far. */
  const thisYear = $derived((details?.founding_months ?? []).reduce((sum, m) => sum + m.count, 0));

  const runningYear = $derived(details?.founding_months?.at(-1)?.year);

  /**
   * The bars: the complete years, then the running one.
   *
   * # The running year is drawn, and drawn differently
   *
   * It used to be a sentence under the chart and not a bar at all, because
   * eight months against twelve reads as a category collapsing rather than as
   * a year that has not finished. Michael asked for it as a bar, so the
   * ambiguity is carried by the colour instead: accent-400 against accent-600,
   * the same hue a step lighter.
   *
   * A lighter step of the same hue rather than a second colour on purpose. A
   * different hue says "a different thing was measured"; this is the same
   * measure, incomplete. The sentence under the chart stays, so the reason is
   * stated in words as well as in colour — nothing here is colour alone.
   */
  const bars = $derived([
    ...years.map((y) => ({ year: y.year, count: y.count, running: false })),
    ...(thisYear > 0 && runningYear ? [{ year: runningYear, count: thisYear, running: true }] : [])
  ]);

  /**
   * The scale spans every bar drawn, the running one included. A partial year
   * drawn to its own scale would be a taller bar for a smaller number.
   */
  const tallestYear = $derived(Math.max(1, ...bars.map((b) => b.count)));

  /**
   * The signal kinds, in one table across the three windows.
   *
   * The comparison is the finding, not any single number: thirty days against a
   * twelfth of the year says whether a category is moving now or is merely
   * doing what it always does. Three columns make that a glance; three separate
   * figures elsewhere on the screen would not.
   */
  const signalKinds = ['new_companies', 'capital', 'location', 'name', 'executives'] as const;
</script>

<svelte:window on:keydown={onKeydown} />

<!--
  Back goes above the title, on the left, which is `ScreenView`'s own back row.

  It used to be a hand-built button in `slot="actions"` — the end of the title
  row, on the right. That slot is for actions on the thing being looked at, and
  leaving is not one of them: it acts on the screen, not on the industry. The
  guide and the company profile both put their way out above the title on the
  left, and this was the one screen still disagreeing.

  The footer keeps its own copy. This screen is longer than the window, and by
  the time somebody has read the register table the header has scrolled away.
-->
<ScreenView
  icon={Building2}
  title={name}
  subtitle={details?.industry_name ?? ''}
  onBack={onClose}
  backLabel={$_('industry.back')}
>
  {#if loading}
    <p class="py-12 text-center text-sm text-gray-500">{$_('industry.reading')}</p>
  {:else if failed || !details}
    <p class="py-12 text-center text-sm text-gray-500">{$_('industry.failed')}</p>
  {:else}
    <div class="space-y-5">
      <!--
        The size of the category, and only the companies.

        Not the contact figure the same API carries: it counts every row we
        hold at these companies, where the customer can only buy a contact
        whose role is one of the ten the filter offers. It advertises about
        two and a half times what a selection returns, and a number that
        wrong does not belong on the screen somebody uses to decide. See
        IndustryTree, which drops it for the same reason.
      -->
      <p class="text-sm text-gray-600">
        {$_('industry.summary', {
          values: { companies: swiss(details.company_count) }
        })}
      </p>

      {#if details.description}
        <p class="max-w-prose text-sm text-gray-600">{details.description}</p>
      {/if}

      <!-- Reach: the least abstract thing here, so it leads. -->
      <section class="rounded-surface border border-hairline p-4">
        <h4 class="flex items-center gap-2 text-sm font-medium text-gray-900">
          <Globe class="h-4 w-4 text-accent-600" />
          {$_('industry.reach.title')}
        </h4>
        <p class="mt-1 max-w-prose text-sm text-gray-600">{$_('industry.reach.body')}</p>

        <!--
          Postal leads: it is the channel that almost always works, so it sets
          the expectation the other four are read against. It is a counted
          figure like the rest, not a constant — a liquidated company can have
          had its domicile revoked.
        -->
        <dl class="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {#each [['postal', details.reach.postal], ['website', details.reach.website], ['email', details.reach.email], ['phone', details.reach.phone], ['linkedin', details.reach.linkedin]] as const as [key, value] (key)}
            <div class="rounded-control bg-surface-sunken px-3 py-2">
              <dt class="text-2xs text-gray-500">{$_(`industry.reach.${key}`)}</dt>
              <dd class="text-sm font-medium tabular-nums text-gray-900">
                {swiss(value)}
                <span class="text-xs font-normal text-gray-500">({share(value)}%)</span>
              </dd>
            </div>
          {/each}
        </dl>
      </section>

      <!-- Size: the gate most customers set before anything else. -->
      <section class="rounded-surface border border-hairline p-4">
        <h4 class="flex items-center gap-2 text-sm font-medium text-gray-900">
          <Users class="h-4 w-4 text-accent-600" />
          {$_('industry.size.title')}
        </h4>
        <p class="mt-1 max-w-prose text-sm text-gray-600">{$_('industry.size.body')}</p>

        <div class="mt-3">
          <SizeBands {bands} />
        </div>
      </section>

      <!-- The legal mix, which explains the size and capital panels. -->
      <section class="rounded-surface border border-hairline p-4">
        <h4 class="flex items-center gap-2 text-sm font-medium text-gray-900">
          <Building2 class="h-4 w-4 text-accent-600" />
          {$_('industry.forms.title')}
        </h4>

        <ul class="mt-3 space-y-1.5">
          {#each forms.rows as row (row.key)}
            <li class="flex items-center justify-between gap-3 text-sm">
              <span class="min-w-0 truncate text-gray-700">
                {$_(`industry.legalForm.${row.key}`)}
              </span>
              <span class="shrink-0 tabular-nums text-gray-500">
                {swiss(row.count)}
                <span class="text-xs">({share(row.count)}%)</span>
              </span>
            </li>
          {/each}

          <!--
            The remainder carries a share like the rows above it. It is
            summed rather than enumerated, not hidden, and a reader adding
            the panel up should reach the category.
          -->
          {#if forms.other > 0}
            <li class="flex items-center justify-between gap-3 text-sm text-gray-500">
              <span>{$_('industry.forms.other')}</span>
              <span class="shrink-0 tabular-nums">
                {swiss(forms.other)}
                <span class="text-xs">({share(forms.other)}%)</span>
              </span>
            </li>
          {/if}
        </ul>
      </section>

      <!-- Where they are: the shape first, the towns beside it. -->
      <section class="rounded-surface border border-hairline p-4">
        <h4 class="flex items-center gap-2 text-sm font-medium text-gray-900">
          <MapPin class="h-4 w-4 text-accent-600" />
          {$_('industry.region.title')}
        </h4>

        <div class="mt-3 sm:flex sm:items-start sm:gap-6">
          {#if spread.length > 0}
            <div class="w-full max-w-[16rem] sm:w-56 sm:shrink-0">
              <SwissMap {spread} />
            </div>
          {/if}

          <div class="mt-3 min-w-0 flex-1 sm:mt-0">
            <ul class="space-y-1.5">
              {#each details.cities.slice(0, 8) as city (city.name)}
                <li class="flex items-center justify-between gap-3 text-sm">
                  <span class="min-w-0 truncate text-gray-700">{city.name}</span>
                  <span class="shrink-0 tabular-nums text-gray-500">{swiss(city.count)}</span>
                </li>
              {/each}
            </ul>

            <!--
              The remainder, stated rather than left to arithmetic. `cities`
              is the twenty largest towns and most of a category lives
              outside them, so a reader who adds this list up and gets a
              third of the total is otherwise entitled to conclude the data
              is broken.
            -->
            {#if details.cities_other > 0}
              <p class="mt-2 text-xs text-gray-500">
                {$_('industry.region.elsewhere', {
                  values: { companies: swiss(details.cities_other) }
                })}
              </p>
            {/if}
          </div>
        </div>
      </section>

      <!-- When they were founded. -->
      {#if years.length > 0}
        <section class="rounded-surface border border-hairline p-4">
          <h4 class="text-sm font-medium text-gray-900">{$_('industry.founding.title')}</h4>

          <!--
            Every bar carries its year, which replaced a pair of labels at the
            two ends. Thirteen four-digit years across this panel is tight —
            9px rather than the 11px of text-2xs, because at 11px they collide
            once the panel is narrow.

            The labels are also what lets the running year be a paler bar: at
            2.72:1 against white, accent-400 is under the 3:1 a mark needs to
            carry meaning on its own, and a visible label beside it is the
            relief that makes it legible.
          -->
          <div class="mt-3 flex items-end gap-1" aria-hidden="true">
            {#each bars as bar (bar.year)}
              <div class="flex min-w-0 flex-1 flex-col items-center gap-1">
                <div
                  class="w-full rounded-t {bar.running ? 'bg-accent-400' : 'bg-accent-600'}"
                  style="height: {Math.max(2, (48 * bar.count) / tallestYear)}px"
                  title="{bar.year}: {swiss(bar.count)}"
                ></div>
                <!--
                  Two digits until sm, four above it. Measured: at a 390px
                  viewport the panel is 308px and a column is 17.4px, where a
                  four-digit year is 20px and the row reads as one unbroken
                  band of digits. The full year is on the bar's title either
                  way, and the sentence below names the running one.
                -->
                <span class="text-[9px] tabular-nums leading-none text-gray-500">
                  <span class="sm:hidden">{String(bar.year).slice(2)}</span>
                  <span class="hidden sm:inline">{bar.year}</span>
                </span>
              </div>
            {/each}
          </div>

          <!--
            The bars are aria-hidden, so this is the same numbers in a form a
            screen reader can read. Before the years were drawn under each bar
            the chart exposed only its two end labels; hiding the row without
            this would have been a step backwards rather than forwards.
          -->
          <ul class="sr-only">
            {#each bars as bar (bar.year)}
              <li>{bar.year}: {swiss(bar.count)}</li>
            {/each}
          </ul>

          <!--
            The sentence stays now that the running year is also a bar: it says
            in words what the lighter colour says in paint, which is the whole
            reason a partial year can be drawn beside complete ones at all.
          -->
          {#if thisYear > 0 && runningYear}
            <p class="mt-2 text-xs text-gray-500">
              {$_('industry.founding.running', {
                values: { companies: swiss(thisYear), year: runningYear }
              })}
            </p>
          {/if}
        </section>
      {/if}

      <!-- What the register has published lately. -->
      <section class="rounded-surface border border-hairline p-4">
        <h4 class="flex items-center gap-2 text-sm font-medium text-gray-900">
          <TrendingUp class="h-4 w-4 text-accent-600" />
          {$_('industry.signals.title')}
        </h4>
        <p class="mt-1 max-w-prose text-sm text-gray-600">{$_('industry.signals.body')}</p>

        <table class="mt-3 w-full text-sm">
          <thead>
            <tr class="text-2xs text-gray-500">
              <th class="py-1 text-left font-normal"></th>
              <th class="py-1 text-right font-normal">{$_('industry.signals.days30')}</th>
              <th class="py-1 text-right font-normal">{$_('industry.signals.days90')}</th>
              <th class="py-1 text-right font-normal">{$_('industry.signals.days365')}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-hairline-subtle">
            {#each signalKinds as kind (kind)}
              <tr>
                <td class="py-1.5 text-gray-700">{$_(`industry.signals.${kind}`)}</td>
                <td class="py-1.5 text-right tabular-nums text-gray-600">
                  {swiss(details.signals.days_30[kind])}
                </td>
                <td class="py-1.5 text-right tabular-nums text-gray-600">
                  {swiss(details.signals.days_90[kind])}
                </td>
                <td class="py-1.5 text-right tabular-nums text-gray-600">
                  {swiss(details.signals.days_365[kind])}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </section>

      <!--
        The date of the data, not of the visit. An import that failed on
        Friday should say Thursday here.
      -->
      {#if details.as_of}
        <p class="text-xs text-gray-400">
          {$_('industry.asOf', { values: { date: details.as_of } })}
        </p>
      {/if}
    </div>
  {/if}

  <svelte:fragment slot="footer">
    <!--
      Back again at the foot, because the screen is longer than the window and
      the header's copy has scrolled away by the time somebody has read the
      register table.
    -->
    <button type="button" onclick={onClose} class="btn btn-secondary">
      {$_('industry.back')}
    </button>

    <!--
      The selection is reachable from here because this is where the decision is
      made. Somebody who opens a category, reads that four in ten have a
      telephone number and decides yes should not have to find the row again to
      say so.
    -->
    {#if onToggle}
      <button type="button" onclick={onToggle} class="btn btn-primary">
        {#if selected}
          <Check class="mr-1.5 inline h-4 w-4" />
          {$_('industry.deselect')}
        {:else}
          {$_('industry.select')}
        {/if}
      </button>
    {/if}
  </svelte:fragment>
</ScreenView>
